import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { CategoryChip } from "./CategoryChip";
import { blogPostPath, formatBlogDate, readingTimeLabel } from "@/lib/blog";

/**
 * Blog kartı — landing FeatureCard pattern'inin blog versiyonu.
 * variant="default" (3-col grid) | "compact" (sidebar)
 */
export function BlogCard({ post, lang = "tr", variant = "default" }) {
  const href = blogPostPath(lang, post.slug);
  const fallbackImg = "/images/og-image.png";

  if (variant === "compact") {
    return (
      <Link
        to={href}
        className="flex gap-3 group hover:bg-slate-50 -mx-2 px-2 py-2 rounded-lg transition-colors"
      >
        <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-slate-100">
          <img
            src={post.featured_image || fallbackImg}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.currentTarget.src = fallbackImg;
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-slate-900 line-clamp-2 group-hover:text-[#22A7D1] transition-colors">
            {post.title}
          </h4>
          <p className="text-xs text-slate-500 mt-1">{formatBlogDate(post.published_at, lang)}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 hover:shadow-xl hover:-translate-y-1.5 transition-all group"
    >
      {/* Görsel */}
      <div className="aspect-[16/9] overflow-hidden bg-slate-100 relative">
        <img
          src={post.featured_image || fallbackImg}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
          onError={(e) => {
            e.currentTarget.src = fallbackImg;
          }}
        />
        {post.is_featured && (
          <div className="absolute top-3 left-3 px-2.5 py-1 bg-white/95 backdrop-blur rounded-full text-xs font-bold text-[#22A7D1] shadow-sm">
            ★ {lang === "en" ? "Featured" : lang === "ar" ? "مميّز" : "Öne Çıkan"}
          </div>
        )}
      </div>

      {/* İçerik */}
      <div className="flex flex-col flex-1 p-5 sm:p-6">
        {post.category_name && (
          <div className="mb-3">
            <CategoryChip
              name={post.category_name}
              slug={post.category_slug}
              color={post.category_color}
              lang={lang}
              size="sm"
              as="span"
            />
          </div>
        )}

        <h3 className="text-lg font-semibold text-slate-900 line-clamp-2 group-hover:text-[#22A7D1] transition-colors leading-snug">
          {post.title}
        </h3>

        {post.excerpt && (
          <p className="mt-2 text-sm text-slate-500 line-clamp-2 leading-relaxed">
            {post.excerpt}
          </p>
        )}

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{formatBlogDate(post.published_at, lang)}</span>
          {post.reading_time && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {readingTimeLabel(post.reading_time, lang)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
